import { useEffect, useRef, useState } from 'react';

/**
 * Renders a Mermaid diagram from ```mermaid fenced code in a doc's markdown.
 * mermaid.js is dynamically imported the first time a diagram appears, so it is
 * a lazy chunk (kept out of the initial bundle) and shared/cached across pages.
 * securityLevel 'strict' sandboxes the diagram source. Renders in the app shell
 * (the .md doc view), so — unlike the sandboxed HTML iframe — scripts run.
 */
let mermaidReady: Promise<typeof import('mermaid').default> | null = null;
function loadMermaid() {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((mod) => {
      const mermaid = mod.default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: 'neutral',
        flowchart: { htmlLabels: true, useMaxWidth: true, curve: 'basis' },
        themeVariables: { primaryColor: '#efe9f5', primaryBorderColor: '#7b4fa8', lineColor: '#7b4fa8', fontSize: '14px' },
      });
      return mermaid;
    });
  }
  return mermaidReady;
}

let seq = 0;

export default function MermaidDiagram({ chart }: { chart: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadMermaid()
      .then(async (mermaid) => {
        const id = `mmd-${++seq}`;
        const { svg } = await mermaid.render(id, chart.trim());
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      })
      .catch((e) => {
        if (!cancelled) setError(String(e?.message ?? e));
      });
    return () => {
      cancelled = true;
    };
  }, [chart]);

  if (error) {
    // Fall back to the raw diagram source so nothing is lost on a render error.
    return (
      <pre className="mermaid-error" title={error}>
        {chart}
      </pre>
    );
  }
  return <div className="mermaid-diagram" ref={ref} aria-label="flow diagram" />;
}
